import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Info, Settings, Users, TrendingUp, CreditCard, UserPlus, Filter, ExternalLink } from 'lucide-react';
import { useAdmin } from '@/contexts/AdminContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInfo, formatRoleName } from '@/hooks/use-user-info';
import { useCanAccess } from '@/components/authnz/AbilityContext';
import type { HelpContent } from '@/components/help-system/help-content';

interface DashboardAccountMenuProps {
  helpContent: HelpContent | undefined;
  onOpenHelp: () => void;
}

export function DashboardAccountMenu({ helpContent, onOpenHelp }: DashboardAccountMenuProps) {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const { roles, activeSubscription, loading: userInfoLoading } = useUserInfo();
  const [isGlobalLogout, setIsGlobalLogout] = React.useState(false);
  let isAdminMode = false;
  let isAuthorizedAdmin = false;
  try {
    const admin = useAdmin();
    isAdminMode = admin.isAdminMode;
    isAuthorizedAdmin = admin.isAuthorizedAdmin;
  } catch (error) {
    // Ignore context error if rendered outside AdminProvider
  }

  let canCreateSubscription = false;
  let canReadProspects = false;
  let canReadFunnel = false;
  try {
    canCreateSubscription = useCanAccess('create', 'Subscription');
    canReadProspects = useCanAccess('read', 'ClientProspect');
    canReadFunnel = useCanAccess('read', 'FunnelAnalytics');
  } catch (error) {
    // Ignore context error if rendered outside AbilityProvider
  }

  const showAdminButtons = isAdminMode && isAuthorizedAdmin;
  const showSalesButtons = !showAdminButtons && (canReadProspects || canReadFunnel);
  const showSubscriptionsShortcut = canCreateSubscription && !showAdminButtons;

  return (
    <div className="flex items-center gap-2.5 flex-shrink-0">
      {showSubscriptionsShortcut && (
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
          onClick={() => navigate('/subscriptions')}
          title="Subscriptions"
          aria-label="Subscriptions"
        >
          <CreditCard className="h-5 w-5" />
        </Button>
      )}
      {showAdminButtons && (
        <div className="flex items-center gap-1.5 mr-1 border-r pr-2.5 border-line">
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
            onClick={() => navigate('/admin/users')}
            title="User Management"
            aria-label="User Management"
          >
            <Users className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
            onClick={() => navigate('/admin/statistics')}
            title="Statistics"
            aria-label="Statistics"
          >
            <TrendingUp className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
            onClick={() => navigate('/sales/prospects')}
            title="Client Prospects"
            aria-label="Client Prospects"
          >
            <UserPlus className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
            onClick={() => navigate('/sales/funnel')}
            title="Funnel Analytics"
            aria-label="Funnel Analytics"
          >
            <Filter className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
            onClick={() => navigate('/subscriptions')}
            title="Subscriptions"
            aria-label="Subscriptions"
          >
            <CreditCard className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
            onClick={() => navigate('/self-assessment')}
            title="Public Self-Assessment"
            aria-label="Public Self-Assessment"
          >
            <ExternalLink className="h-5 w-5" />
          </Button>
        </div>
      )}
      {showSalesButtons && (
        <div className="flex items-center gap-1.5 mr-1 border-r pr-2.5 border-line">
          {canReadProspects && (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
              onClick={() => navigate('/sales/prospects')}
              title="Client Prospects"
              aria-label="Client Prospects"
            >
              <UserPlus className="h-5 w-5" />
            </Button>
          )}
          {canReadFunnel && (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
              onClick={() => navigate('/sales/funnel')}
              title="Funnel Analytics"
              aria-label="Funnel Analytics"
            >
              <Filter className="h-5 w-5" />
            </Button>
          )}
        </div>
      )}
      <Button
        variant="outline"
        size="icon"
        className="h-10 w-10 shrink-0 rounded-lg border-line bg-surface text-fg hover:border-primary hover:bg-surface hover:text-primary"
        onClick={() => navigate('/settings')}
        aria-label="Settings"
      >
        <Settings className="h-5 w-5" />
      </Button>
      {authLoading ? (
        <Skeleton className="h-10 w-10 rounded-full" />
      ) : user ? (
        <TooltipProvider>
          <Tooltip>
            <DropdownMenu>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label="Account menu"
                  >
                    <Avatar className="h-10 w-10">
                      {user.photoURL && (
                        <AvatarImage
                          src={user.photoURL}
                          alt={user.displayName || user.email || 'User avatar'}
                          referrerPolicy="no-referrer"
                        />
                      )}
                      <AvatarFallback className="text-xs font-medium">
                        {user.email?.[0]?.toUpperCase() ?? '?'}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 max-w-[calc(100vw-1.5rem)] border-line bg-surface text-fg shadow-2xl shadow-black/40"
              >
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1.5">
                    <span className="break-all font-medium">{user.email}</span>
                    {user.displayName &&
                      user.displayName !== user.email &&
                      /\s/.test(user.displayName) && (
                        <span className="text-xs text-muted">{user.displayName}</span>
                      )}
                    {!userInfoLoading && (
                      <>
                        {roles.length > 0 && (
                          <div className="flex flex-col gap-1 pt-1">
                            <span className="text-xs font-medium text-muted">Role:</span>
                            <div className="flex flex-wrap gap-1">
                              {roles.map((role) => (
                                <Badge key={role} className="border-line bg-bg text-xs text-fg hover:bg-bg">
                                  {formatRoleName(role)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {activeSubscription && (
                          <div className="flex flex-col gap-1 pt-1">
                            <span className="text-xs font-medium text-muted">Subscription:</span>
                            <Badge className="text-xs w-fit bg-primary text-on-accent hover:bg-primary">
                              {activeSubscription.status === 'active' ? 'Active' : activeSubscription.status}
                            </Badge>
                          </div>
                        )}
                        {!activeSubscription && roles.length > 0 && (
                          <div className="flex flex-col gap-1 pt-1">
                            <span className="text-xs font-medium text-muted">Subscription:</span>
                            <Badge className="text-xs w-fit border-line bg-bg text-fg hover:bg-bg">
                              None
                            </Badge>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-line" />
                {helpContent && (
                  <DropdownMenuItem onClick={onOpenHelp} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                    <HelpCircle className="h-4 w-4" />
                    Help
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/help')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                  <HelpCircle className="h-4 w-4" />
                  Help videos
                </DropdownMenuItem>
                {showAdminButtons && (
                  <>
                    <DropdownMenuSeparator className="bg-line" />
                    <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted">
                      Admin & Sales UIs
                    </DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => navigate('/admin/users')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                      <Users className="h-4 w-4" />
                      User Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin/statistics')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                      <TrendingUp className="h-4 w-4" />
                      Statistics
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/sales/prospects')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                      <UserPlus className="h-4 w-4" />
                      Client Prospects
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/sales/funnel')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                      <Filter className="h-4 w-4" />
                      Funnel Analytics
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/subscriptions')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                      <CreditCard className="h-4 w-4" />
                      Subscriptions
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/self-assessment')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                      <ExternalLink className="h-4 w-4" />
                      Public Self-Assessment
                    </DropdownMenuItem>
                  </>
                )}
                {showSalesButtons && (
                  <>
                    <DropdownMenuSeparator className="bg-line" />
                    <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted">
                      Sales
                    </DropdownMenuLabel>
                    {canReadProspects && (
                      <DropdownMenuItem onClick={() => navigate('/sales/prospects')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                        <UserPlus className="h-4 w-4" />
                        Client Prospects
                      </DropdownMenuItem>
                    )}
                    {canReadFunnel && (
                      <DropdownMenuItem onClick={() => navigate('/sales/funnel')} className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg">
                        <Filter className="h-4 w-4" />
                        Funnel Analytics
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    document.querySelector<HTMLButtonElement>('[data-testid="feedback-button"]')?.click();
                  }}
                  className="flex items-center gap-2 text-fg focus:bg-bg focus:text-fg"
                >
                  Send feedback
                </DropdownMenuItem>
                <DropdownMenuCheckboxItem
                  checked={isGlobalLogout}
                  onCheckedChange={(checked) => setIsGlobalLogout(checked === true)}
                  onSelect={(e) => e.preventDefault()}
                  className="text-fg focus:bg-bg focus:text-fg"
                >
                  <div className="flex items-center gap-2">
                    <span>Sign out everywhere</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted hover:text-fg" />
                        </TooltipTrigger>
                        <TooltipContent className="border-line bg-surface text-fg">
                          <p className="max-w-[200px]">When checked, you will be signed out from all devices and browsers.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator className="bg-line" />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout({ global: isGlobalLogout });
                    navigate('/');
                  }}
                  className="text-fg focus:bg-bg focus:text-fg"
                >
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TooltipContent side="bottom" className="border-line bg-surface text-fg">
              <div className="text-xs">{user.email}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-xs sm:text-sm text-muted">Not signed in</span>
      )}
    </div>
  );
}
